/**
 * Topology persistence hooks — the endpoint SLD Studio did not have.
 *
 * The studio's banner used to read "Local editing only — there is no topology endpoint yet,
 * so nothing here is persisted. Leaving SLD Studio discards it." That was accurate. An
 * engineer could seed a plant from the device inventory, spend an hour correcting the feeds
 * a site survey disagreed with, hit browser-back, and lose all of it. This is the client for
 * `GET|PUT|DELETE /sites/:id/topology`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW TO WIRE IT  (this file is not wired into SldStudioView; here is exactly how)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * 1 · LOAD ON MOUNT
 *
 *      const stored = useTopology(siteId);
 *
 *    `stored.data === undefined` — still loading. Render nothing editable yet; mounting the
 *    editor with a blank document and then replacing it is how you get a flash of an empty
 *    canvas followed by the real plant.
 *    `stored.data === null`      — NO STORED TOPOLOGY. Seed locally, exactly as the studio
 *                                  does today: `buildTopologyFromDevices(...)`, a preset, or
 *                                  `blankTopology(siteId, siteName)`. A 404 is normalised to
 *                                  `null` because "nothing saved yet" is the ordinary state
 *                                  of a site nobody has drawn, not an error to surface.
 *    `stored.data` is an object  — use `stored.data.doc` as the editor's initial document and
 *                                  keep `stored.data.version` as the version to send back.
 *
 * 2 · HOLD THE VERSION, AND HOLD IT IN A REF
 *
 *      const versionRef = useRef<number | undefined>(undefined);
 *      // set from the load, then from every successful save
 *
 *    Every accepted save bumps the server's version, so the number changes more often than a
 *    render does. A version captured in a debounced closure is stale by construction, and a
 *    stale version is a 409 the user did nothing to deserve. `undefined` means "nothing is
 *    stored" and is the ONLY correct value for a first save.
 *
 * 3 · AUTOSAVE, DEBOUNCED, AND NEVER TWO AT ONCE
 *
 *      const save = useSaveTopology(siteId);
 *
 *      // on doc change:  if (dirty) debounce(1500) -> tick()
 *      // on save settle: tick()                       <-- THE CLAUSE THAT IS EASY TO MISS
 *      //
 *      // function tick() {
 *      //   if (!dirtyRef.current) return;
 *      //   if (save.isPending) return;   // the settle handler will call tick() again
 *      //   save.mutate({ doc: docRef.current, ...version });
 *      // }
 *
 *    Debounce around 1.5s. The serialisation matters as much as the debounce: if a second
 *    save fires while the first is still in flight it sends the pre-bump version and
 *    conflicts with its OWN predecessor — a 409 the user cannot make sense of, because the
 *    only other editor was themselves. So a tick that lands mid-flight must be SKIPPED.
 *
 *    AND A SKIPPED TICK MUST BE RE-ARMED, which is why `onSettled` calls `tick()` too. This
 *    recipe used to end at "skip the tick while `save.isPending` and let the next one carry
 *    the newer document", and that is wrong: the next tick only exists if the user makes
 *    another edit. Traced with a 2500 ms round trip —
 *
 *        1501ms  fire     drag-1
 *        3103ms  SKIPPED  rename-1  (save in flight)
 *        4003ms  settled  drag-1
 *        persisted = ['drag-1']            <-- rename-1 never left the browser
 *
 *    The user renamed two nodes, stopped editing, saw a successful save, and lost the rename
 *    on navigation. It only reproduces when the round trip OUTLASTS the debounce, which is
 *    why it hides in development: at 900 ms latency a skip cannot happen at all. A 400 KB
 *    document from a field tablet is exactly the slow case, and this module's own header
 *    calls that pair — "one in the field and one at a desk" — the normal one.
 *
 *      save.mutate({ doc, ...(versionRef.current !== undefined
 *                             ? { version: versionRef.current } : {}) });
 *
 *    Send the document EXACTLY as the editor holds it. Do not re-key it, do not stamp the
 *    site id onto it, do not strip or add a field. If either side rewrote a field, the
 *    editor's dirty check would flip straight back to dirty and the autosave would fire
 *    again, forever, once per debounce interval for as long as the tab stayed open. The
 *    server also rejects unknown keys with a 422, which is deliberate: it is what keeps
 *    solver output (`_served`, `_lost`, `_soc`) out of a document that is supposed to record
 *    only what was drawn.
 *
 *    DO NOT DIRTY-CHECK BY STRING COMPARE. The round trip is deep-equal, NOT byte-identical:
 *    Postgres `jsonb` does not preserve object key order, so a document sent as
 *    `["siteId","siteName","nominal","nodes","origin"]` comes back as
 *    `["nodes","origin","siteId","nominal","siteName"]` — same keys, same values,
 *    `JSON.stringify(sent) === JSON.stringify(read)` FALSE. A stringify-based dirty check is
 *    therefore permanently dirty and produces precisely the infinite autosave loop described
 *    above. Track dirtiness from edit events (what the studio does), or compare structurally.
 *
 * 4 · ON A 409 — THE ONE CASE THAT NEEDS A DECISION
 *
 *      onError: (err) => {
 *        const clash = topologyConflict(err);
 *        if (!clash) return;                      // a real error; surface it normally
 *        if (clash.current === null) {
 *          // An admin reverted this site to unsaved while the tab was open. The local
 *          // diagram is still here and is now the only copy — clear the version and save
 *          // it as a new topology to keep it.
 *          versionRef.current = undefined;
 *          return;
 *        }
 *        // Someone else saved while this tab was editing. `clash.current` is their
 *        // document, already in hand, so the user can be shown both without another
 *        // request. Two ways out, and the user picks:
 *        //   ADOPT THEIRS    — set the editor's doc to clash.current.doc
 *        //                     and versionRef.current = clash.current.version
 *        //   KEEP MINE       — versionRef.current = clash.current.version, then save again
 *        //                     (that overwrites theirs, knowingly, which is the point)
 *        // What must NOT happen is a silent retry with the new version: that is
 *        // last-write-wins with extra steps, and it is what the 409 exists to prevent.
 *      }
 *
 *    Do not auto-resolve this. The server refuses to guess which diagram is right and so
 *    should the client.
 *
 * 5 · REVERTING
 *
 *      const revert = useDeleteTopology(siteId);   // admin only; 403 for anyone else
 *
 *    On success the cache goes to `null`, so the studio is back to "nothing stored". The
 *    editor's own document is untouched — the caller decides whether to clear the canvas.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THESE QUERIES DO NOT REFETCH
 *
 * `useTopology` is `staleTime: Infinity` with focus and reconnect refetching off, which is
 * the opposite of every other hook in this folder. A topology is not a reading — it is the
 * editor's own state. A background refetch that swapped the document under someone mid-edit
 * would destroy work in the most confusing way available. The way a client learns that
 * somebody else saved is the 409 on its next save, which is designed to carry their document
 * with it. If a screen genuinely needs to pull a newer version, call `refetch()` explicitly
 * and only when the editor is not dirty.
 */
import { PRODUCT } from '@/product/profile';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SiteTopology, TopologyOrigin } from '@/lib/topology/model';
import type { TopologyAccess } from '@/lib/topology/reach';
import { ApiError, api } from './client';

/** A topology as stored. `doc` is the document; everything else is about the row. */
export interface StoredTopology {
  siteId: string;
  /** Byte-identical to what was last saved. */
  doc: SiteTopology;
  origin: TopologyOrigin;
  nodeCount: number;
  /** What the next save must send back. Bumped by the server on every accepted save. */
  version: number;
  createdAt: string;
  updatedAt: string;
  /** Snapshotted server-side, so it survives that user being deleted. */
  updatedByEmail: string | null;
  /**
   * WHICH SECTIONS OF THIS DOCUMENT THE VIEWER MAY SEE. Authoritative; server-resolved.
   *
   * The document itself arrives WHOLE even for a viewer who reaches part of the site, and that
   * is decided rather than overlooked. `lumen-be src/modules/topology/reach.ts` carries the
   * argument in full; the three load-bearing halves of it are that this GET and the PUT are one
   * read-modify-write cycle, so anything stripped here is deleted from the stored row by the
   * next autosave; that a stripped document fails the API's own schema on the first dangling
   * conductor; and that the redundancy verdict has to keep describing the whole plant
   * (`Docs/08-views/SLD-STUDIO.md` §5) or it is a Tier claim no standard makes.
   *
   * So `narrowTopology(doc, access)` is what a renderer draws, and `deriveRedundancy(doc)` —
   * the WHOLE doc — is what it grades. Never the other way round.
   */
  access: TopologyAccess;
}

export interface SaveTopologyInput {
  doc: SiteTopology;
  /**
   * The version this client loaded. OMIT IT — do not pass `undefined` explicitly at the
   * wire level — only for a first save, when nothing is stored. Omitting it once a topology
   * exists is refused with a 409 rather than treated as consent to overwrite.
   */
  version?: number | undefined;
}

/** Exported so a caller can invalidate or seed this exact entry without guessing the key. */
export function topologyQueryKey(siteId: string | null): readonly unknown[] {
  return ['topology', siteId] as const;
}

/**
 * The stored topology, or `null` when the site has none.
 *
 * A 404 becomes `null` rather than an error: an undrawn site is the ordinary starting state,
 * and making every caller pattern-match on `error.status === 404` to discover it would get
 * that wrong somewhere.
 */
export function useTopology(siteId: string | null) {
  return useQuery({
    queryKey: topologyQueryKey(siteId),
    /*
     * Not asked at all by a product that draws no power chain (`PRODUCT.drawsPowerChain`): a
     * Cratus site never has a diagram, so every site answered `topology-not-drawn` once per
     * session to every console — 83 404s in one local afternoon, read back as an error storm.
     * Disabled, `data` stays undefined, which every caller already reads as "nothing drawn".
     */
    enabled: siteId !== null && PRODUCT.drawsPowerChain,
    queryFn: async (): Promise<StoredTopology | null> => {
      try {
        return await api.get<StoredTopology>(`/sites/${siteId}/topology`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    /* See the header: this is editor state, not a reading. A background refetch that
     * swapped the document under an active edit would destroy work. */
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    /* A 4xx means the request itself is wrong, so retrying only delays the error the user
     * needs to see. Mirrors the policy in queries.ts. */
    retry: (count, error) =>
      error instanceof ApiError && error.isClientFault ? false : count < 2,
  });
}

/**
 * Create or replace the site's topology.
 *
 * On success the cache is written directly from the response — never invalidated. An
 * invalidation would trigger a refetch, and a refetch is exactly what must not happen while
 * an editor is open (see the header). Writing the response also keeps the cached `version`
 * in step, which is what the next autosave depends on.
 */
export function useSaveTopology(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveTopologyInput) =>
      api.put<StoredTopology>(`/sites/${siteId}/topology`, {
        doc: input.doc,
        // Conditional spread: under exactOptionalPropertyTypes an explicit `undefined` is
        // not an absent key, and here the difference is load-bearing — an absent version
        // means "first save", a present one means "I loaded this".
        ...(input.version !== undefined ? { version: input.version } : {}),
      }),
    onSuccess: (saved) => {
      qc.setQueryData(topologyQueryKey(siteId), saved);
    },
    /* A 409 is a decision for the user, not a transient failure, and every other 4xx is a
     * malformed document. Retrying either is pointless; retrying a 409 would be actively
     * harmful, because the retry would still carry the same stale version.
     *
     * A 403 joined that list when site scope reached this endpoint: the server refuses a save
     * from a viewer who reaches only PART of the site, because the studio's seeders rebuild the
     * document from a device list that is itself narrowed, and one click would drop the plants
     * they cannot see. The studio hides its save controls for such a viewer (`access.wholeSite`),
     * so a 403 arriving here means a control was left reachable — not something to retry. */
    retry: false,
  });
}

/**
 * Revert the site to having no stored topology. Admin only — a 403 for anyone else.
 *
 * The local document is not touched. The caller decides whether reverting also means
 * clearing the canvas.
 */
export function useDeleteTopology(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<void>(`/sites/${siteId}/topology`),
    onSuccess: () => {
      qc.setQueryData(topologyQueryKey(siteId), null);
    },
    retry: false,
  });
}

/** What a losing save gets back. */
export interface TopologyConflictInfo {
  /** Server-written, and written once so every client explains the clash the same way. */
  detail: string;
  /**
   * The topology as actually stored, or `null` when it was deleted while this client was
   * editing. Carried in the 409 body itself so the user can be shown both documents without
   * a second round trip — during which a third save could land and stale the comparison.
   */
  current: StoredTopology | null;
}

/**
 * Narrow a save error to a version conflict, or `null` if it is anything else.
 *
 * `current` is an RFC 9457 extension member, so it is not on `ProblemDetails` and has to be
 * read through a cast. Doing that here once means no caller has to know that.
 */
export function topologyConflict(err: unknown): TopologyConflictInfo | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const problem = err.problem as typeof err.problem & { current?: StoredTopology | null };
  return {
    detail: problem.detail,
    current: problem.current ?? null,
  };
}
